
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Upload, X, Save, Image } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useFileUpload } from "@/hooks/notifications/useFileUpload";

interface Team {
  id: string;
  name: string;
  image_url?: string;
}

export const TeamLogoManager = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploading, setUploading] = useState<string | null>(null);
  const { toast } = useToast();
  const { uploadFile } = useFileUpload();

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const { data, error } = await supabase
        .from('teams')
        .select('id, name, image_url')
        .order('name');

      if (error) throw error;
      setTeams(data || []);
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast({
        title: "Error",
        description: "Failed to fetch teams",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (teamId: string, file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    setUploading(teamId);
    
    try {
      // Upload file to storage
      const publicUrl = await uploadFile(file, 'team-logos', `team-${teamId}-${Date.now()}`);
      
      if (!publicUrl) {
        throw new Error('Failed to upload file');
      }

      // Update team with new logo URL
      const { error } = await supabase
        .from('teams')
        .update({ image_url: publicUrl })
        .eq('id', teamId);

      if (error) throw error;

      // Update local state
      setTeams(teams.map(team => 
        team.id === teamId 
          ? { ...team, image_url: publicUrl }
          : team
      ));

      toast({
        title: "Success",
        description: "Team logo uploaded successfully",
      });
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: "Upload failed",
        description: "Failed to upload team logo",
        variant: "destructive",
      });
    } finally {
      setUploading(null);
    }
  };

  const handleRemoveLogo = async (teamId: string) => {
    try {
      const { error } = await supabase
        .from('teams')
        .update({ image_url: null })
        .eq('id', teamId);

      if (error) throw error;

      setTeams(teams.map(team => 
        team.id === teamId 
          ? { ...team, image_url: undefined }
          : team
      ));

      toast({
        title: "Success",
        description: "Team logo removed successfully",
      });
    } catch (error) {
      console.error('Error removing logo:', error);
      toast({
        title: "Error",
        description: "Failed to remove team logo",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Team Logos</CardTitle>
          <CardDescription>Loading teams...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Image className="h-5 w-5" />
          Team Logos
        </CardTitle>
        <CardDescription>
          Upload and manage logos for each team. These logos will be displayed throughout the application.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {teams.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            No teams found. Create teams first to upload logos.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teams.map((team) => (
              <div
                key={team.id}
                className="border rounded-lg p-4 space-y-3 hover:shadow-md transition-shadow"
              >
                <div className="text-center">
                  <h3 className="font-medium text-sm truncate" title={team.name}>
                    {team.name}
                  </h3>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  {team.image_url ? (
                    <div className="relative">
                      <img
                        src={team.image_url}
                        alt={`${team.name} logo`}
                        className="w-16 h-16 object-cover rounded-lg border"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                      <Button
                        size="sm"
                        variant="destructive"
                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                        onClick={() => handleRemoveLogo(team.id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <Image className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                  
                  <div className="w-full">
                    <Label htmlFor={`logo-${team.id}`} className="sr-only">
                      Upload logo for {team.name}
                    </Label>
                    <Input
                      id={`logo-${team.id}`}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleFileUpload(team.id, file);
                        }
                      }}
                      disabled={uploading === team.id}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() => document.getElementById(`logo-${team.id}`)?.click()}
                      disabled={uploading === team.id}
                    >
                      {uploading === team.id ? (
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          Uploading...
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Upload className="h-3 w-3" />
                          {team.image_url ? 'Replace' : 'Upload'}
                        </div>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
